import cx from "classnames";
import { FormikProps } from "formik";
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { Column, Row } from "@gamedev-web/gamedev-sdk-core";
import { BasicButton, Icon, IconSize } from "@gaming/xds-react-js";
import { ChevronLeft } from "@gaming/xds-react-js-icons";

import { getDashboardUrl, getPitchDashboardUrl } from "../../helpers/navigationHelpers";
import { useProgramColorPicker } from "../../helpers/useProgramColorPicker";
import { useStrings } from "../../localization/localizationHelpers";
import { OnboardingErrorType } from "../../models/errors";
import { Programs } from "../../models/programsConfig";
import { RouteNames } from "../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../partnerConfig/routes";
import { programsActions } from "../../redux/actions/programsActions";
import {
  getCurrentError,
  getCurrentGameConcept,
  getCurrentPitchConcept,
  getHasAtLeastOneAcceptedApplication,
  getIsSavingForm,
} from "../../redux/selectors";
import { OnboardingPageAction } from "../../telemetry/models";
import { logPageAction } from "../../telemetry/pageAction";
import { LoadingSpinner } from "../Spinner";
import * as styles from "./SaveAndExitButton.module.css";

type SaveAndExitButtonProps = Readonly<{
  formId: string;
  subFormId: string;
  formRef: React.RefObject<FormikProps<any>>;
  programDashboardRouteName: RouteNames;
  onSaveToCrm: () => void;
  program: Programs;
}>;

export const SaveAndExitButton = ({
  formId,
  subFormId,
  formRef,
  programDashboardRouteName,
  onSaveToCrm,
  program,
}: SaveAndExitButtonProps) => {
  const [saveAndExit, saveSubmitFormErrorMessage] = useStrings(
    "saveAndExit",
    "saveSubmitFormErrorMessage"
  );
  const atLeastOneAcceptedApplication = useSelector(getHasAtLeastOneAcceptedApplication);
  const currentGameConcept = useSelector(getCurrentGameConcept);
  const currentPitchConcept = useSelector(getCurrentPitchConcept);
  const dispatch = useDispatch();
  const history = useHistory();
  const isSavingFormToCrm = useSelector(getIsSavingForm);
  const currentError = useSelector(getCurrentError);
  const [isSubFormInSaveState, setIsSubFormInSaveState] = useState(false);
  const [isSaveButtonClicked, setIsSaveButtonClicked] = useState(false);
  const colorStyle = useProgramColorPicker();

  const mapOfProgramRoutes = new Map([
    [Programs.Aaa, RouteNames.AaaDashboard],
    [Programs.IdAzure, RouteNames.IdAzureHome],
    [Programs.IdXbox, RouteNames.IdXboxWelcome],
    [Programs.Pitch, RouteNames.PitchHome],
  ]);

  const routeByProgram = mapOfProgramRoutes.get(program);

  const onClick = () => {
    const values = formRef?.current?.values;
    logPageAction(OnboardingPageAction.SaveAndExitFormButton);
    // save data in redux
    dispatch(
      programsActions.updateSubFormData(program, {
        formId,
        subFormId,
        subForm: values,
      })
    );
    onSaveToCrm();
    setIsSaveButtonClicked(true);
  };

  useEffect(() => {
    if (isSaveButtonClicked) {
      if (isSavingFormToCrm) {
        setIsSubFormInSaveState(true);
      } else {
        if (
          isSubFormInSaveState &&
          currentError?.type !== OnboardingErrorType.Save &&
          routeByProgram
        ) {
          switch (program) {
            case Programs.IdXbox:
              if (atLeastOneAcceptedApplication) {
                history.push(getDashboardUrl(programDashboardRouteName, currentGameConcept?.id));
              } else {
                history.push(getDashboardUrl(programDashboardRouteName));
              }
              break;
            case Programs.Aaa:
              history.push(getDashboardUrl(programDashboardRouteName, currentGameConcept?.id));
              break;
            case Programs.Pitch:
              history.push(getPitchDashboardUrl(currentPitchConcept?.id));
              break;
            default:
              history.push(OnboardingUrls[routeByProgram]);
              break;
          }
        }
        setIsSubFormInSaveState(false);
      }
    }
  }, [
    atLeastOneAcceptedApplication,
    currentError,
    currentGameConcept,
    currentPitchConcept,
    history,
    isSaveButtonClicked,
    isSavingFormToCrm,
    isSubFormInSaveState,
    program,
    programDashboardRouteName,
    routeByProgram,
  ]);

  if (isSubFormInSaveState) {
    return (
      <Row>
        <Column className={cx(styles.column, styles.spinnerWrapper)}>
          <LoadingSpinner />
        </Column>
      </Row>
    );
  }

  return (
    <Row>
      <Column className={styles.column}>
        <BasicButton
          as={"button"}
          className={styles.button}
          onClick={onClick}
          onKeyDown={e => e.key === "Enter" && onClick()}
          role="link"
        >
          <Icon className={styles.chevronLeftIcon} icon={ChevronLeft} iconSize={IconSize.Custom} />
          <p className={cx(styles.buttonText, colorStyle)}>{saveAndExit}</p>
        </BasicButton>
        {currentError?.type === OnboardingErrorType.Save && (
          <span className={styles.errorMessage}>{saveSubmitFormErrorMessage}</span>
        )}
      </Column>
    </Row>
  );
};
